import { environment } from '../config';
import React, { useEffect, useState, useCallback } from 'react';
import TvRefreshNotice, { TvRefreshProblem } from '../components/TvRefreshNotice';

const TV_TOKEN = new URLSearchParams(window.location.search).get('token') || '';
const API_BASE = environment.apiUrl;

// Wake lock — prevents screen from sleeping
function useWakeLock() {
  useEffect(() => {
    let wakeLock: any = null;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
        }
      } catch (_) {}
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      wakeLock?.release();
    };
  }, []);
}

// TV scale helper — renders at 1920x1080 then scales to fit any screen
function useTvScale() {
  const [scale, setScale] = React.useState(1);
  React.useEffect(() => {
    const calc = () => {
      const sx = window.innerWidth / 1920;
      const sy = window.innerHeight / 1080;
      setScale(Math.min(sx, sy));
    };
    calc();
    window.addEventListener('resize', calc);
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => window.removeEventListener('resize', calc);
  }, []);
  return scale;
}

// Fullscreen state tracker - always re-enters fullscreen when exited (same as TV Calendar)
function useFullscreenLock() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const enterFullscreen = useCallback(() => {
    const el = document.documentElement as any;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const fs = !!(document as any).fullscreenElement || !!(document as any).webkitFullscreenElement;
      setIsFullscreen(fs);
      // Always re-enter fullscreen when exited
      if (!fs) setTimeout(enterFullscreen, 500);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [enterFullscreen]);

  return { isFullscreen, enterFullscreen };
}

const MEDALS = ['🥇', '🥈', '🥉'];
const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

interface SalesmanStats {
  id: string; name: string; rank: number; score: number;
  sold: number; doorsKnocked: number; appointments: number;
  callbacks: number; conversionRate: number;
  earnedCommission: number; pendingCommission: number;
}

function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return time;
}

function Clock() {
  const t = useClock();
  const timeStr = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const dateStr = t.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 36, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{timeStr}</div>
      <div style={{ fontSize: 14, color: '#8FA8C0', marginTop: 4 }}>{dateStr}</div>
    </div>
  );
}

export default function TvLeaderboardPage() {
  const scale = useTvScale();
  useWakeLock();
  const { isFullscreen, enterFullscreen } = useFullscreenLock();
  const [valid, setValid] = useState<boolean | null>(null);
  const [allTime, setAllTime] = useState<SalesmanStats[]>([]);
  const [weekly, setWeekly] = useState<SalesmanStats[]>([]);
  const [view, setView] = useState<'allTime' | 'weekly'>('allTime');
  const [countdown, setCountdown] = useState(15);
  const [fade, setFade] = useState(true);
  const [refreshProblem, setRefreshProblem] = useState<TvRefreshProblem>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/tv/leaderboard?token=${encodeURIComponent(TV_TOKEN)}`, { cache: 'no-store' });
      if (!r.ok) {
        setRefreshProblem(r.status === 401 || r.status === 403 ? 'access' : 'connection');
        return;
      }
      const d = await r.json();
      setAllTime(d.allTime || []);
      setWeekly(d.weekly || []);
      setLastUpdated(new Date());
      setRefreshProblem(null);
    } catch { setRefreshProblem('connection'); }
  }, []);

  // Verify token
  useEffect(() => {
    fetch(`${API_BASE}/tv/verify?token=${encodeURIComponent(TV_TOKEN)}`, { cache: 'no-store' })
      .then(r => setValid(r.ok))
      .catch(() => setValid(false));
  }, []);

  // Fetch data + refresh every 60s
  useEffect(() => {
    if (!valid) return;
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, [valid, fetchData]);

  // Rotate view every 15s with countdown
  useEffect(() => {
    if (!valid) return;
    const t = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setFade(false);
          setTimeout(() => {
            setView(v => v === 'allTime' ? 'weekly' : 'allTime');
            setFade(true);
          }, 400);
          return 15;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [valid]);

  // Removed: full page refresh was breaking fullscreen on Fire TV
  // Data refreshes every 60s via fetchData interval instead

  if (valid === null) return (
    <div style={{ background: '#0F1923', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#4A7EB5', fontSize: 24 }}>Loading...</div>
    </div>
  );

  if (!valid) return (
    <div style={{ background: '#0F1923', width: '100vw', height: '100vh', position: 'relative' }}>
      <TvRefreshNotice problem="access" lastUpdated={null} />
    </div>
  );

  const data = view === 'allTime' ? allTime : weekly;
  const displayed = data.slice(0, 8);

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', background: '#0F1923' }} onClick={!isFullscreen ? enterFullscreen : undefined}>
      {!isFullscreen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,25,35,0.85)', cursor: 'pointer' }}>
          <div style={{ color: '#fff', fontSize: 32, fontWeight: 700, textAlign: 'center', pointerEvents: 'none' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>⛶</div>
            Tap anywhere to go fullscreen
          </div>
        </div>
      )}
    <div style={{ width: 1920, height: 1080, transform: `scale(${scale})`, transformOrigin: 'top left', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: '#0F1923' }}>

      <TvRefreshNotice problem={refreshProblem} lastUpdated={lastUpdated} />

      {/* Header */}
      <div style={{ background: '#1A2B3C', borderBottom: '2px solid #4A7EB5', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/logo-white.png" alt="McMullen Browne" style={{ height: 48, width: 'auto', objectFit: 'contain' }} />
          <div style={{ background: '#4A7EB522', border: '1px solid #4A7EB544', borderRadius: 8, padding: '4px 14px', fontSize: 14, fontWeight: 700, color: '#4A7EB5', textTransform: 'uppercase', letterSpacing: 1 }}>🏠 Roofing Leaderboard</div>
        </div>
        <Clock />
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, padding: '16px 32px 0', flexShrink: 0 }}>
        {(['allTime', 'weekly'] as const).map(v => (
          <div key={v} style={{
            padding: '10px 28px', fontSize: 16, fontWeight: 700, cursor: 'default',
            color: view === v ? '#fff' : '#4A6580',
            background: view === v ? '#4A7EB5' : '#1A2B3C',
            borderRadius: v === 'allTime' ? '10px 0 0 10px' : '0 10px 10px 0',
            border: '1px solid ' + (view === v ? '#4A7EB5' : '#2D4259'),
            transition: 'all 0.3s',
          }}>
            {v === 'allTime' ? '🏆 All Time' : '📅 This Week'}
          </div>
        ))}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingLeft: 20, gap: 10 }}>
          <div style={{ flex: 1, height: 4, background: '#2D4259', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${((15 - countdown) / 15) * 100}%`, height: '100%', background: '#4A7EB5', transition: 'width 1s linear', borderRadius: 2 }} />
          </div>
          <div style={{ color: '#4A6580', fontSize: 13, whiteSpace: 'nowrap' }}>
            Switching to {view === 'allTime' ? 'This Week' : 'All Time'} in {countdown}s
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div style={{ flex: 1, padding: '16px 32px 24px', opacity: fade ? 1 : 0, transition: 'opacity 0.4s' }}>
        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 140px 100px 100px 100px 160px 160px', gap: 0, padding: '0 16px 12px', marginBottom: 4 }}>
          {['', 'Name', 'Score', 'Sold', '🚪', '📅', '💰 Earned', '⏳ Pending'].map((h, i) => (
            <div key={i} style={{ color: '#4A6580', fontSize: 18, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
          ))}
        </div>

        {displayed.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#4A6580', fontSize: 24, marginTop: 60 }}>No data yet</div>
        ) : (
          displayed.map((s, i) => {
            const isTop3 = i < 3;
            const medalColor = isTop3 ? MEDAL_COLORS[i] : null;
            return (
              <div key={s.id} style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr 140px 100px 100px 100px 160px 160px',
                gap: 0,
                background: i % 2 === 0 ? '#1A2B3C' : '#1E3040',
                borderRadius: 12,
                padding: '0 16px',
                marginBottom: 10,
                borderLeft: isTop3 ? `4px solid ${medalColor}` : '4px solid transparent',
                alignItems: 'center',
                minHeight: 88,
              }}>
                <div style={{ fontSize: isTop3 ? 36 : 26, color: isTop3 ? medalColor! : '#4A6580', fontWeight: 800 }}>
                  {isTop3 ? MEDALS[i] : `#${s.rank}`}
                </div>
                <div style={{ fontWeight: 800, color: '#fff', fontSize: 32, letterSpacing: -0.3 }}>{s.name}</div>
                <div style={{ fontWeight: 900, color: i === 0 ? '#FFD700' : '#4A7EB5', fontSize: 42 }}>{s.score}</div>
                <div style={{ fontWeight: 700, color: '#10B981', fontSize: 28 }}>{s.sold}</div>
                <div style={{ color: '#8FA8C0', fontSize: 26 }}>{s.doorsKnocked}</div>
                <div style={{ color: '#8FA8C0', fontSize: 26 }}>{s.appointments}</div>
                <div style={{ fontWeight: 700, color: '#10B981', fontSize: 26 }}>${s.earnedCommission.toLocaleString()}</div>
                <div style={{ fontWeight: 600, color: '#F59E0B', fontSize: 26 }}>${s.pendingCommission.toLocaleString()}</div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer legend */}
      <div style={{ background: '#1A2B3C', borderTop: '1px solid #2D4259', padding: '16px 32px', display: 'flex', gap: 32, flexShrink: 0 }}>
        {[['💰', 'Sold', '500pts'], ['📅', 'Appt', '2pts'], ['👍', 'Lead', '2pts'], ['📞', 'CB', '2pts'], ['↩️', 'Go Back', '2pts'], ['🚪', 'Door', '1pt'], ['📈', 'Conv >20%', '+15pts']].map(([icon, label, pts]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4A6580', fontSize: 20 }}>
            <span style={{ fontSize: 24 }}>{icon}</span><span style={{ color: '#8FA8C0' }}>{label}</span><span style={{ color: '#4A7EB5', fontWeight: 700 }}>{pts}</span>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}
